import { useEffect, useRef, useState, useCallback } from "react";
import { Canvas as FabricCanvas, Circle, Rect, PencilBrush, util, IText } from "fabric";
import type { FabricObject } from "fabric";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useTranslation } from "react-i18next";
import { useWhiteboardSync, WhiteboardEvent } from "@/hooks/useWhiteboardSync";
import { jsPDF } from "jspdf";
import {
  MousePointer2,
  Pencil,
  Square,
  Circle as CircleIcon,
  Eraser,
  Trash2,
  Undo2,
  Redo2,
  Download,
  FileText,
  Type,
  X,
  Users,
  Wifi,
  WifiOff,
} from "lucide-react";

type Tool = "select" | "draw" | "rectangle" | "circle" | "eraser" | "text";

const COLORS = [
  "#000000",
  "#ffffff",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
];

interface CollaborativeWhiteboardProps {
  meetingId: string;
  userId: string;
  userName: string;
  onClose?: () => void;
  className?: string;
}

export function CollaborativeWhiteboard({
  meetingId,
  userId,
  userName,
  onClose,
  className = "",
}: CollaborativeWhiteboardProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [fabricCanvas, setFabricCanvas] = useState<FabricCanvas | null>(null);
  const [activeTool, setActiveTool] = useState<Tool>("draw");
  const [activeColor, setActiveColor] = useState("#000000");
  const [brushSize, setBrushSize] = useState(4);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedo = useRef(false);
  const isRemoteUpdate = useRef(false);
  const objectIdMap = useRef(new Map<string, FabricObject>());

  // Handle remote events
  const handleRemoteEvent = useCallback(
    (event: WhiteboardEvent) => {
      if (!fabricCanvas) return;
      isRemoteUpdate.current = true;

      switch (event.type) {
        case "add":
          // Deserialize and add object
          util.enlivenObjects([event.payload.object]).then((objects) => {
            const obj = objects[0] as FabricObject | undefined;
            if (obj) {
              objectIdMap.current.set(event.payload.id, obj);
              (obj as any).__syncId = event.payload.id;
              fabricCanvas.add(obj);
              fabricCanvas.renderAll();
            }
          });
          break;

        case "modify": {
          const objToModify = objectIdMap.current.get(event.payload.id);
          if (objToModify) {
            objToModify.set(event.payload.properties);
            objToModify.setCoords();
            fabricCanvas.renderAll();
          }
          break;
        }

        case "remove": {
          const objToRemove = objectIdMap.current.get(event.payload.id);
          if (objToRemove) {
            fabricCanvas.remove(objToRemove);
            objectIdMap.current.delete(event.payload.id);
            fabricCanvas.renderAll();
          }
          break;
        }

        case "clear":
          fabricCanvas.clear();
          fabricCanvas.backgroundColor = "#ffffff";
          objectIdMap.current.clear();
          fabricCanvas.renderAll();
          break;

        case "draw":
          // For path objects from drawing
          util.enlivenObjects([event.payload.object]).then((objects) => {
            const obj = objects[0] as FabricObject | undefined;
            if (obj) {
              objectIdMap.current.set(event.payload.id, obj);
              (obj as any).__syncId = event.payload.id;
              fabricCanvas.add(obj);
              fabricCanvas.renderAll();
            }
          });
          break;
      }

      isRemoteUpdate.current = false;
    },
    [fabricCanvas]
  );

  const { users, isConnected, broadcastEvent, broadcastCursor } =
    useWhiteboardSync({
      meetingId,
      userId,
      userName,
      onRemoteEvent: handleRemoteEvent,
    });

  // Generate unique ID for objects
  const generateObjectId = useCallback(() => {
    return `${userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, [userId]);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const canvas = new FabricCanvas(canvasRef.current, {
      width: container.clientWidth,
      height: container.clientHeight - 100,
      backgroundColor: "#ffffff",
      isDrawingMode: true,
    });

    canvas.freeDrawingBrush = new PencilBrush(canvas);
    canvas.freeDrawingBrush.color = activeColor;
    canvas.freeDrawingBrush.width = brushSize;

    setFabricCanvas(canvas);

    const initialState = JSON.stringify(canvas.toJSON());
    setHistory([initialState]);
    setHistoryIndex(0);

    const handleResize = () => {
      canvas.setDimensions({
        width: container.clientWidth,
        height: container.clientHeight - 100,
      });
      canvas.renderAll();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      canvas.dispose();
    };
  }, []);

  // Handle cursor movement
  useEffect(() => {
    if (!canvasRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      broadcastCursor(x, y);
    };

    const canvas = canvasRef.current;
    canvas.addEventListener("mousemove", handleMouseMove);

    return () => {
      canvas.removeEventListener("mousemove", handleMouseMove);
    };
  }, [broadcastCursor]);

  // Sync canvas events
  useEffect(() => {
    if (!fabricCanvas) return;

    const handleObjectAdded = (e: any) => {
      if (isUndoRedo.current || isRemoteUpdate.current) return;

      const obj = e.target;
      if (!obj || (obj as any).__syncId) return;

      const syncId = generateObjectId();
      (obj as any).__syncId = syncId;
      objectIdMap.current.set(syncId, obj);

      const objJson = obj.toJSON();
      broadcastEvent("add", { id: syncId, object: objJson });

      // Save to history
      const state = JSON.stringify(fabricCanvas.toJSON());
      setHistory((prev) => [...prev.slice(0, historyIndex + 1), state]);
      setHistoryIndex((prev) => prev + 1);
    };

    const handleObjectModified = (e: any) => {
      if (isUndoRedo.current || isRemoteUpdate.current) return;

      const obj = e.target;
      if (!obj) return;

      const syncId = (obj as any).__syncId;
      if (!syncId) return;

      broadcastEvent("modify", {
        id: syncId,
        properties: {
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          angle: obj.angle,
        },
      });
    };

    const handlePathCreated = (e: any) => {
      if (isRemoteUpdate.current) return;

      const path = e.path;
      if (!path || (path as any).__syncId) return;

      const syncId = generateObjectId();
      (path as any).__syncId = syncId;
      objectIdMap.current.set(syncId, path);

      const pathJson = path.toJSON();
      broadcastEvent("draw", { id: syncId, object: pathJson });
    };

    fabricCanvas.on("object:added", handleObjectAdded);
    fabricCanvas.on("object:modified", handleObjectModified);
    fabricCanvas.on("path:created", handlePathCreated);

    return () => {
      fabricCanvas.off("object:added", handleObjectAdded);
      fabricCanvas.off("object:modified", handleObjectModified);
      fabricCanvas.off("path:created", handlePathCreated);
    };
  }, [fabricCanvas, broadcastEvent, generateObjectId, historyIndex]);

  // Update brush settings
  useEffect(() => {
    if (!fabricCanvas) return;

    if (activeTool === "draw" || activeTool === "eraser") {
      fabricCanvas.isDrawingMode = true;
      if (fabricCanvas.freeDrawingBrush) {
        fabricCanvas.freeDrawingBrush.color =
          activeTool === "eraser" ? "#ffffff" : activeColor;
        fabricCanvas.freeDrawingBrush.width =
          activeTool === "eraser" ? brushSize * 3 : brushSize;
      }
    } else {
      fabricCanvas.isDrawingMode = false;
    }
  }, [activeTool, activeColor, brushSize, fabricCanvas]);

  const handleToolClick = useCallback(
    (tool: Tool) => {
      setActiveTool(tool);

      if (!fabricCanvas) return;

      if (tool === "rectangle") {
        const rect = new Rect({
          left: 100,
          top: 100,
          fill: activeColor,
          width: 100,
          height: 80,
          strokeWidth: 2,
          stroke: activeColor,
        });
        fabricCanvas.add(rect);
        fabricCanvas.setActiveObject(rect);
        setActiveTool("select");
      } else if (tool === "circle") {
        const circle = new Circle({
          left: 100,
          top: 100,
          fill: activeColor,
          radius: 50,
          strokeWidth: 2,
          stroke: activeColor,
        });
        fabricCanvas.add(circle);
        fabricCanvas.setActiveObject(circle);
        setActiveTool("select");
      } else if (tool === "text") {
        const text = new IText(t("whiteboard.tools.textPlaceholder"), {
          left: 100,
          top: 100,
          fill: activeColor,
          fontSize: 24,
          fontFamily: "Arial",
        });
        fabricCanvas.add(text);
        fabricCanvas.setActiveObject(text);
        text.enterEditing();
        text.selectAll();
        setActiveTool("select");
      }
    },
    [fabricCanvas, activeColor, t]
  );

  const handleClear = useCallback(() => {
    if (!fabricCanvas) return;
    fabricCanvas.clear();
    fabricCanvas.backgroundColor = "#ffffff";
    objectIdMap.current.clear();
    fabricCanvas.renderAll();
    broadcastEvent("clear", {});
  }, [fabricCanvas, broadcastEvent]);

  const handleUndo = useCallback(() => {
    if (historyIndex <= 0 || !fabricCanvas) return;

    isUndoRedo.current = true;
    const newIndex = historyIndex - 1;
    fabricCanvas.loadFromJSON(JSON.parse(history[newIndex])).then(() => {
      fabricCanvas.renderAll();
      setHistoryIndex(newIndex);
      isUndoRedo.current = false;
    });
  }, [fabricCanvas, history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1 || !fabricCanvas) return;

    isUndoRedo.current = true;
    const newIndex = historyIndex + 1;
    fabricCanvas.loadFromJSON(JSON.parse(history[newIndex])).then(() => {
      fabricCanvas.renderAll();
      setHistoryIndex(newIndex);
      isUndoRedo.current = false;
    });
  }, [fabricCanvas, history, historyIndex]);

  const handleSavePNG = useCallback(() => {
    if (!fabricCanvas) return;

    const dataURL = fabricCanvas.toDataURL({
      format: "png",
      quality: 1,
      multiplier: 2,
    });

    const link = document.createElement("a");
    link.download = `whiteboard-${meetingId}-${Date.now()}.png`;
    link.href = dataURL;
    link.click();
  }, [fabricCanvas, meetingId]);

  const handleSavePDF = useCallback(() => {
    if (!fabricCanvas) return;

    const dataURL = fabricCanvas.toDataURL({
      format: "png",
      quality: 1,
      multiplier: 2,
    });

    const canvasWidth = fabricCanvas.getWidth();
    const canvasHeight = fabricCanvas.getHeight();
    
    // Determine orientation based on canvas dimensions
    const orientation = canvasWidth > canvasHeight ? "landscape" : "portrait";
    const pdf = new jsPDF({
      orientation,
      unit: "px",
      format: [canvasWidth, canvasHeight],
    });

    pdf.addImage(dataURL, "PNG", 0, 0, canvasWidth, canvasHeight);
    pdf.save(`whiteboard-${meetingId}-${Date.now()}.pdf`);
  }, [fabricCanvas, meetingId]);

  const tools = [
    { id: "select" as Tool, icon: MousePointer2, label: t("whiteboard.tools.select") },
    { id: "draw" as Tool, icon: Pencil, label: t("whiteboard.tools.draw") },
    { id: "text" as Tool, icon: Type, label: t("whiteboard.tools.text") },
    { id: "rectangle" as Tool, icon: Square, label: t("whiteboard.tools.rectangle") },
    { id: "circle" as Tool, icon: CircleIcon, label: t("whiteboard.tools.circle") },
    { id: "eraser" as Tool, icon: Eraser, label: t("whiteboard.tools.eraser") },
  ];

  return (
    <div
      ref={containerRef}
      className={`flex flex-col h-full bg-background ${className}`}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 p-2 border-b bg-card flex-wrap">
        <div className="flex items-center gap-1 flex-wrap">
          {/* Connection Status */}
          <Badge
            variant={isConnected ? "default" : "destructive"}
            className="mr-2"
          >
            {isConnected ? (
              <Wifi className="h-3 w-3 mr-1" />
            ) : (
              <WifiOff className="h-3 w-3 mr-1" />
            )}
            {isConnected ? t("whiteboard.connected") : t("whiteboard.disconnected")}
          </Badge>

          {/* Tools */}
          {tools.map((tool) => (
            <Tooltip key={tool.id}>
              <TooltipTrigger asChild>
                <Button
                  variant={activeTool === tool.id ? "default" : "ghost"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleToolClick(tool.id)}
                >
                  <tool.icon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{tool.label}</TooltipContent>
            </Tooltip>
          ))}

          <div className="w-px h-6 bg-border mx-1" />

          {/* Colors */}
          <div className="flex items-center gap-1">
            {COLORS.map((color) => (
              <button
                key={color}
                className={`h-6 w-6 rounded-full border-2 transition-transform ${
                  activeColor === color
                    ? "scale-110 border-primary"
                    : "border-transparent hover:scale-105"
                }`}
                style={{ backgroundColor: color }}
                onClick={() => setActiveColor(color)}
              />
            ))}
          </div>

          <div className="w-px h-6 bg-border mx-1" />

          {/* Brush Size */}
          <div className="flex items-center gap-2 w-24">
            <Slider
              value={[brushSize]}
              onValueChange={(value) => setBrushSize(value[0])}
              min={1}
              max={20}
              step={1}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Participants */}
          {users.length > 0 && (
            <div className="flex items-center gap-1 mr-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <div className="flex -space-x-2">
                {users.slice(0, 5).map((user) => (
                  <Tooltip key={user.id}>
                    <TooltipTrigger asChild>
                      <Avatar className="h-6 w-6 border-2 border-background">
                        <AvatarFallback
                          style={{ backgroundColor: user.color }}
                          className="text-xs text-white"
                        >
                          {user.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent>{user.name}</TooltipContent>
                  </Tooltip>
                ))}
                {users.length > 5 && (
                  <Badge variant="secondary" className="h-6 text-xs">
                    +{users.length - 5}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleUndo}
                disabled={historyIndex <= 0}
              >
                <Undo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("whiteboard.actions.undo")}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleRedo}
                disabled={historyIndex >= history.length - 1}
              >
                <Redo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("whiteboard.actions.redo")}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleClear}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("whiteboard.actions.clear")}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleSavePNG}
              >
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("whiteboard.actions.savePNG")}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleSavePDF}
              >
                <FileText className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("whiteboard.actions.savePDF")}</TooltipContent>
          </Tooltip>

          {onClose && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Canvas with remote cursors */}
      <div className="flex-1 overflow-hidden relative">
        <canvas ref={canvasRef} className="w-full h-full" />

        {/* Remote Cursors */}
        {users.map(
          (user) =>
            user.cursor && (
              <div
                key={user.id}
                className="absolute pointer-events-none z-50 transition-all duration-75"
                style={{
                  left: user.cursor.x,
                  top: user.cursor.y,
                  transform: "translate(-2px, -2px)",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }}
                >
                  <path
                    d="M5.5 3.21V20.79C5.5 21.13 5.67 21.45 5.96 21.62C6.24 21.79 6.59 21.78 6.87 21.59L11.76 18.3L15.34 22.84C15.54 23.1 15.83 23.25 16.14 23.25C16.36 23.25 16.57 23.18 16.76 23.04C17.24 22.68 17.33 22 16.97 21.52L13.44 17.05L19.5 14.57C19.81 14.45 20.02 14.15 20.02 13.82C20.01 13.48 19.79 13.19 19.47 13.08L6.47 3.08C6.18 2.88 5.81 2.89 5.54 3.1C5.26 3.31 5.14 3.66 5.23 4L5.5 3.21Z"
                    fill={user.color}
                    stroke="white"
                    strokeWidth="1"
                  />
                </svg>
                <span
                  className="absolute top-5 left-4 text-xs px-1.5 py-0.5 rounded whitespace-nowrap text-white"
                  style={{ backgroundColor: user.color }}
                >
                  {user.name}
                </span>
              </div>
            )
        )}
      </div>
    </div>
  );
}

export default CollaborativeWhiteboard;
